import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, ArrowUpRight } from "lucide-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const SERVICES = [
  "Home & Office Moving",
  "Doorstep Delivery",
  "Van Hire",
  "Packing & Handling",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  from_postcode: "",
  to_postcode: "",
  service: "",
  notes: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.from_postcode || !form.to_postcode || !form.service || !date) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (SITE_KEY && !captchaToken) {
      toast.error("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", {
        ...form,
        move_date: format(date, "yyyy-MM-dd"),
        captcha_token: captchaToken,
      });
      toast.success("Quote request sent — we'll be in touch shortly.");
      setForm(initial);
      setDate(null);
      setCaptchaToken("");
      captchaRef.current?.resetCaptcha();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--jb-navy)]/25 py-4 text-[16px] text-[var(--jb-navy)] placeholder:text-[var(--jb-muted)] focus:outline-none focus:border-[var(--jb-rust)] transition-colors";

  return (
    <section id="quote" className="bg-[var(--jb-bone)] py-24 md:py-40 relative overflow-hidden" data-testid="quote-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5"
          >
            <div className="mono-cap text-[var(--jb-rust)] mb-6">(Quote Request)</div>
            <h2 className="serif text-[clamp(40px,5.5vw,88px)] leading-[0.98] tracking-tight text-[var(--jb-navy)]">
              Get an <br />
              instant, <br />
              <span className="italic">honest quote.</span>
            </h2>
            <p className="mt-8 text-[15px] leading-[1.65] text-[var(--jb-muted)] max-w-[420px]">
              Tell us where you're going and when. We'll come back with a flat, no-surprise price — usually within business hours.
            </p>
            <div className="mt-10 flex flex-wrap gap-6 mono-cap text-[var(--jb-navy)]">
              <span>+44 7396 764997</span>
              <span className="text-[var(--jb-rust)]">·</span>
              <span>info@jayblisslogistics.co.uk</span>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 bg-white p-8 md:p-12 border border-[var(--jb-line)]"
            data-testid="quote-form"
          >
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Full name</label>
                <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" data-testid="quote-name" />
              </div>
              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Phone</label>
                <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07123 456789" data-testid="quote-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="mono-cap text-[var(--jb-muted)]">Email</label>
                <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="quote-email" />
              </div>
              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Moving from (postcode)</label>
                <input value={form.from_postcode} onChange={set("from_postcode")} className={inputCls} placeholder="BT1 1AA" data-testid="quote-from" />
              </div>
              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Moving to (postcode)</label>
                <input value={form.to_postcode} onChange={set("to_postcode")} className={inputCls} placeholder="BT7 3AA" data-testid="quote-to" />
              </div>

              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Date of move</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`${inputCls} text-left flex items-center justify-between`}
                      data-testid="quote-date"
                    >
                      <span>{date ? format(date, "d MMMM yyyy") : "Select a date"}</span>
                      <CalendarIcon className="w-4 h-4 text-[var(--jb-rust)]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border border-[var(--jb-line)]" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="mono-cap text-[var(--jb-muted)]">Service required</label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className={`${inputCls} h-auto rounded-none`} data-testid="quote-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-[var(--jb-line)]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="mono-cap text-[var(--jb-muted)]">Additional notes</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Access, stairs, fragile items, preferred time…"
                  data-testid="quote-notes"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex flex-col gap-3">
                {SITE_KEY && (
                  <div data-testid="quote-captcha">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={SITE_KEY}
                      onVerify={(t) => setCaptchaToken(t)}
                      onExpire={() => setCaptchaToken("")}
                      onError={() => setCaptchaToken("")}
                      theme="light"
                    />
                  </div>
                )}
                <p className="text-[12px] text-[var(--jb-muted)] max-w-[380px]">
                  By submitting, you agree to be contacted about your enquiry. We never share your details.
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-8 py-4 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="submit-quote-btn"
              >
                {loading ? "Sending…" : "Send Quote Request"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
